import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { registerRestaurant, getReferenceCountries, getReferenceCities, getReferenceCuisineTypes } from '../services/api';
import { Eye, EyeOff, ChevronDown, Utensils, ShoppingBag } from 'lucide-react';
import namkeAuthBanner from '../assets/namke-auth-banner.png';
import namkeLogoHeader from '../assets/namke-logo-header.png';
import { mergeAddressLines } from '../utils/address';

const inputClass =
  'w-full min-h-[54px] px-6 bg-namke-cream/60 border border-namke-black/10 rounded-full focus:outline-none focus:ring-2 focus:ring-namke-darkGreen/40 focus:border-transparent transition-colors text-namke-black placeholder-namke-black/40 text-base';

const dropdownButtonClass =
  `${inputClass} flex items-center justify-between gap-2 text-left`;

const BUSINESS_TYPES = [
  {
    id: 'restaurant',
    label: 'Restaurant',
    icon: Utensils,
    title: 'Ouvrez votre boutique',
    subtitle: 'Publiez vos plats du jour, suivez vos versements chaque lundi.',
    namePlaceholder: 'Nom commercial du restaurant',
    nameLabel: 'Nom du restaurant',
    addressLabel: 'Adresse du restaurant',
    showCuisine: true,
  },
  {
    id: 'grocery',
    label: 'Épicerie',
    icon: ShoppingBag,
    title: 'Ouvrez votre épicerie',
    subtitle: 'Mettez en vente vos produits africains et du monde entier.',
    namePlaceholder: 'Nom de votre magasin',
    nameLabel: 'Nom du magasin',
    addressLabel: 'Adresse du magasin',
    showCuisine: false,
  },
];

const RestaurantSignup = () => {
  const navigate = useNavigate();
  const [businessType, setBusinessType] = useState('restaurant');
  const [formData, setFormData] = useState({
    email: '',
    phone_country: '',
    phone: '',
    store_address: '',
    floor_suite: '',
    address_extra: '',
    store_name: '',
    password: '',
    city: '',
    description: '',
    cuisine_type: '',
  });
  const [countryList, setCountryList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [cuisineList, setCuisineList] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [refLoading, setRefLoading] = useState(true);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showCuisineDropdown, setShowCuisineDropdown] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const countryDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);
  const cuisineDropdownRef = useRef(null);

  const currentType = BUSINESS_TYPES.find((t) => t.id === businessType);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setRefLoading(true);
        const [countries, cuisines] = await Promise.all([
          getReferenceCountries(),
          getReferenceCuisineTypes(),
        ]);
        if (cancelled) return;
        const cl = Array.isArray(countries) ? countries : [];
        const cul = Array.isArray(cuisines) ? cuisines : [];
        setCountryList(cl);
        setCuisineList(cul);
        if (cl.length > 0) {
          setSelectedCountryId(cl[0].id);
          setFormData((prev) => ({ ...prev, phone_country: cl[0].code }));
        }
      } catch (e) {
        console.error(e);
        setError('Impossible de charger pays et types de cuisine. Réessayez plus tard.');
      } finally {
        if (!cancelled) setRefLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (selectedCountryId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const cities = await getReferenceCities(selectedCountryId);
        if (cancelled) return;
        setCityList(Array.isArray(cities) ? cities : []);
        const co = countryList.find((c) => c.id === selectedCountryId);
        if (co) {
          setFormData((prev) => ({ ...prev, phone_country: co.code, city: '' }));
          setShowCityDropdown(false);
        }
      } catch (e) {
        console.error(e);
        setCityList([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCountryId, countryList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) setShowCountryDropdown(false);
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) setShowCityDropdown(false);
      if (cuisineDropdownRef.current && !cuisineDropdownRef.current.contains(event.target)) setShowCuisineDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const selectedCountry =
        countryList.find((c) => c.id === selectedCountryId) || countryList.find((c) => c.code === formData.phone_country);
      if (!selectedCountry) { setError('Veuillez sélectionner un pays.'); setLoading(false); return; }
      if (cityList.length > 0 && !String(formData.city || '').trim()) {
        setError('Veuillez choisir une ville.'); setLoading(false); return;
      }
      const dial = selectedCountry.dial_code || '+33';
      const fullPhone = `${dial}${formData.phone.replace(/^\+?\d+/, '')}`;
      const city = formData.city || formData.store_address.split(',').at(-1)?.trim() || '';

      const payload = {
        email: formData.email,
        password: formData.password,
        name: formData.store_name,
        address: mergeAddressLines(formData.store_address, formData.floor_suite, formData.address_extra),
        phone: fullPhone,
        city,
        description: formData.description || undefined,
        ...(currentType.showCuisine && formData.cuisine_type ? { cuisine_type: formData.cuisine_type } : {}),
        business_type: businessType,
      };

      await registerRestaurant(payload);
      navigate('/business/login', {
        replace: true,
        state: { message: 'Inscription réussie. Connectez-vous avec votre e-mail et votre mot de passe.' },
      });
    } catch (err) {
      const msg = err?.message || '';
      if (/existe déjà|already registered|already exists/i.test(msg) && /email|e-mail/i.test(msg)) {
        setError('Cette adresse e-mail est déjà utilisée. Connectez-vous ou choisissez un autre e-mail.');
      } else {
        setError(msg || "Erreur lors de l'inscription");
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedCountry =
    countryList.find((c) => c.id === selectedCountryId) || countryList.find((c) => c.code === formData.phone_country) || countryList[0];

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))', background: '#FCFBF5' }}>
      {/* Colonne formulaire */}
      <div style={{ padding: 'clamp(24px, 4vw, 56px) clamp(20px, 5vw, 72px)', display: 'flex', flexDirection: 'column', gap: 'clamp(20px, 2.6vw, 32px)' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start' }}>
          <img src={namkeLogoHeader} alt="namke" style={{ height: '64px', width: 'auto', objectFit: 'contain' }} />
        </Link>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '22px', maxWidth: '460px', width: '100%', margin: '0 auto' }}>
          <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#EF4C23' }}>Espace pro</span>
            <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1.04, letterSpacing: '-0.025em' }}>
              {currentType.title}
            </h1>
            <p style={{ margin: 0, fontSize: '15.5px', lineHeight: 1.6, color: 'rgba(5,16,4,0.74)' }}>{currentType.subtitle}</p>
          </div>

          {/* Toggle type d'activité */}
          <div className="animate-fade-in-delay" style={{ display: 'flex', gap: '4px', background: '#FBF9D9', borderRadius: '999px', padding: '5px', alignSelf: 'flex-start' }}>
            {BUSINESS_TYPES.map((t) => {
              const Icon = t.icon;
              const active = businessType === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { setBusinessType(t.id); setError(''); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px', border: 'none', cursor: 'pointer',
                    fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', padding: '11px 22px', borderRadius: '999px',
                    background: active ? '#054625' : 'transparent', color: active ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
                    transition: 'background-color 150ms ease, color 150ms ease',
                  }}
                >
                  <Icon size={15} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {error && <div className="form-message form-message--error text-center">{error}</div>}
          {refLoading && (
            <p style={{ fontSize: '13.5px', textAlign: 'center', color: 'rgba(5,16,4,0.55)' }}>Chargement des pays et options…</p>
          )}

          <form onSubmit={handleSubmit} className="animate-fade-in-delay-2" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>E-mail</span>
              <input type="email" name="email" className={inputClass} placeholder="contact@votre-enseigne.com" value={formData.email} onChange={handleChange} required />
            </label>

            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ position: 'relative', flex: 'none' }} ref={countryDropdownRef}>
                <button
                  type="button"
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  className={dropdownButtonClass}
                  style={{ minWidth: '108px' }}
                >
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>{formData.phone_country}</span>
                  <ChevronDown size={16} style={{ transform: showCountryDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease', flexShrink: 0 }} />
                </button>
                {showCountryDropdown && (
                  <div style={{ position: 'absolute', zIndex: 20, marginTop: '6px', width: '260px', maxHeight: '240px', overflowY: 'auto', background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '20px', boxShadow: '0 16px 32px rgba(5,16,4,0.14)', padding: '6px' }}>
                    {countryList.map((country) => (
                      <button
                        key={country.id} type="button"
                        onClick={() => { setSelectedCountryId(Number(country.id)); setShowCountryDropdown(false); }}
                        style={{ width: '100%', textAlign: 'left', padding: '10px 14px', borderRadius: '14px', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(251,249,217,0.8)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span>{country.name}</span>
                        <span style={{ color: 'rgba(5,16,4,0.5)' }}>{country.dial_code}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <input
                type="tel" name="phone" className={inputClass} style={{ flex: 1, minWidth: 0 }}
                placeholder={selectedCountry?.dial_code || '+33'} value={formData.phone} onChange={handleChange} required
              />
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>{currentType.addressLabel}</span>
              <input type="text" name="store_address" className={inputClass} placeholder="Adresse complète" value={formData.store_address} onChange={handleChange} required />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>
                Étage / suite <span style={{ textTransform: 'none', fontWeight: 400, color: 'rgba(5,16,4,0.4)' }}>(optionnel)</span>
              </span>
              <input type="text" name="floor_suite" className={inputClass} placeholder="Ex. 2ᵉ étage" value={formData.floor_suite} onChange={handleChange} />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>
                Complément d'adresse <span style={{ textTransform: 'none', fontWeight: 400, color: 'rgba(5,16,4,0.4)' }}>(optionnel)</span>
              </span>
              <input type="text" name="address_extra" className={inputClass} placeholder="Ex. Code d'accès, bâtiment B" value={formData.address_extra} onChange={handleChange} />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>{currentType.nameLabel}</span>
              <input type="text" name="store_name" className={inputClass} placeholder={currentType.namePlaceholder} value={formData.store_name} onChange={handleChange} required />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Mot de passe</span>
              <span className="flex items-center gap-3" style={{ border: '1px solid rgba(5,16,4,0.1)', background: 'rgba(251,249,217,0.6)', borderRadius: '999px', padding: '0 6px 0 22px', minHeight: '54px' }}>
                <input
                  type={showPassword ? 'text' : 'password'} name="password" placeholder="Minimum 6 caractères"
                  value={formData.password} onChange={handleChange} required minLength={6}
                  style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '16px', color: '#051004', outline: 'none' }}
                />
                <button
                  type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{ border: 'none', cursor: 'pointer', background: '#FCFBF5', color: '#054625', display: 'grid', placeItems: 'center', width: '38px', height: '38px', borderRadius: '999px' }}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </span>
            </label>

            <div style={{ position: 'relative' }} ref={cityDropdownRef}>
              <span style={{ display: 'block', fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', marginBottom: '8px' }}>Ville</span>
              <button
                type="button"
                onClick={() => cityList.length > 0 && setShowCityDropdown(!showCityDropdown)}
                disabled={cityList.length === 0}
                className={dropdownButtonClass}
                style={{ width: '100%', opacity: cityList.length === 0 ? 0.6 : 1, cursor: cityList.length === 0 ? 'not-allowed' : 'pointer' }}
              >
                <span style={{ color: formData.city ? '#051004' : 'rgba(5,16,4,0.4)', fontWeight: formData.city ? 600 : 400 }}>
                  {formData.city || '— Choisir une ville —'}
                </span>
                <ChevronDown size={16} style={{ transform: showCityDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease', flexShrink: 0 }} />
              </button>
              {showCityDropdown && cityList.length > 0 && (
                <div style={{ position: 'absolute', zIndex: 20, marginTop: '6px', width: '100%', maxHeight: '240px', overflowY: 'auto', background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '20px', boxShadow: '0 16px 32px rgba(5,16,4,0.14)', padding: '6px' }}>
                  {cityList.map((city) => (
                    <button
                      key={city.id} type="button"
                      onClick={() => { setFormData((prev) => ({ ...prev, city: city.name })); setShowCityDropdown(false); setError(''); }}
                      style={{ width: '100%', textAlign: 'left', padding: '10px 14px', borderRadius: '14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(251,249,217,0.8)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      {city.name}
                    </button>
                  ))}
                </div>
              )}
              {cityList.length === 0 && selectedCountryId && !refLoading && (
                <p style={{ marginTop: '6px', fontSize: '12.5px', color: '#B8340F' }}>Aucune ville pour ce pays. Contactez l'administrateur.</p>
              )}
            </div>

            {currentType.showCuisine && (
              <div style={{ position: 'relative' }} ref={cuisineDropdownRef}>
                <span style={{ display: 'block', fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', marginBottom: '8px' }}>
                  Type de cuisine <span style={{ textTransform: 'none', fontWeight: 400, color: 'rgba(5,16,4,0.4)' }}>(optionnel)</span>
                </span>
                <button
                  type="button"
                  onClick={() => setShowCuisineDropdown(!showCuisineDropdown)}
                  className={dropdownButtonClass}
                  style={{ width: '100%' }}
                >
                  <span style={{ color: formData.cuisine_type ? '#051004' : 'rgba(5,16,4,0.4)', fontWeight: formData.cuisine_type ? 600 : 400 }}>
                    {formData.cuisine_type || '— Choisir —'}
                  </span>
                  <ChevronDown size={16} style={{ transform: showCuisineDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease', flexShrink: 0 }} />
                </button>
                {showCuisineDropdown && (
                  <div style={{ position: 'absolute', zIndex: 20, marginTop: '6px', width: '100%', maxHeight: '240px', overflowY: 'auto', background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '20px', boxShadow: '0 16px 32px rgba(5,16,4,0.14)', padding: '6px' }}>
                    <button
                      type="button"
                      onClick={() => { setFormData((prev) => ({ ...prev, cuisine_type: '' })); setShowCuisineDropdown(false); setError(''); }}
                      style={{ width: '100%', textAlign: 'left', padding: '10px 14px', borderRadius: '14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px', color: 'rgba(5,16,4,0.5)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(251,249,217,0.8)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      — Aucun —
                    </button>
                    {cuisineList.map((ct) => (
                      <button
                        key={ct.id} type="button"
                        onClick={() => { setFormData((prev) => ({ ...prev, cuisine_type: ct.name })); setShowCuisineDropdown(false); setError(''); }}
                        style={{ width: '100%', textAlign: 'left', padding: '10px 14px', borderRadius: '14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(251,249,217,0.8)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {ct.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>
                Description <span style={{ textTransform: 'none', fontWeight: 400, color: 'rgba(5,16,4,0.4)' }}>(optionnel)</span>
              </span>
              <textarea
                name="description" value={formData.description} onChange={handleChange} rows={3}
                placeholder={businessType === 'grocery' ? 'Présentez votre épicerie, vos spécialités…' : 'Présentez votre établissement…'}
                style={{ width: '100%', padding: '16px 22px', background: 'rgba(251,249,217,0.6)', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '24px', fontFamily: 'Poppins, sans-serif', fontSize: '15px', color: '#051004', outline: 'none', resize: 'vertical' }}
              />
            </label>

            <motion.button
              type="submit"
              disabled={loading || refLoading || countryList.length === 0}
              whileHover={loading ? {} : { scale: 1.015 }}
              whileTap={loading ? {} : { scale: 0.985 }}
              style={{ border: 'none', cursor: loading ? 'default' : 'pointer', background: '#EF4C23', color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16.5px', minHeight: '56px', borderRadius: '999px', opacity: loading || refLoading || countryList.length === 0 ? 0.7 : 1 }}
            >
              {loading ? 'Création du compte…' : 'Créer mon compte pro'}
            </motion.button>
          </form>

          <p className="animate-fade-in-delay-3" style={{ margin: 0, fontSize: '13px', textAlign: 'center', color: 'rgba(5,16,4,0.55)' }}>
            En continuant, vous acceptez nos{' '}
            <Link to="/terms-of-service" style={{ fontWeight: 600 }}>conditions d'utilisation</Link>{' '}
            et notre{' '}
            <Link to="/privacy-policy" style={{ fontWeight: 600 }}>politique de confidentialité</Link>.
          </p>

          <div className="animate-fade-in-delay-3" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ textAlign: 'center', fontSize: '15px', color: 'rgba(5,16,4,0.74)' }}>
              Déjà un compte ? <Link to="/business/login" style={{ fontWeight: 600 }}>Connexion espace pro</Link>
            </span>
            <span style={{ textAlign: 'center', fontSize: '13.5px', color: 'rgba(5,16,4,0.55)' }}>
              Vous êtes client ? <Link to="/signup" style={{ fontWeight: 600, color: '#054625' }}>Inscription client</Link>
            </span>
          </div>
        </div>

        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.58)' }}>© 2026 namke · Berlin</span>
      </div>

      {/* Aside */}
      <aside style={{ position: 'relative', background: '#054625', color: '#FBF9D9', minHeight: '420px', padding: 'clamp(24px, 3.5vw, 52px)', display: 'flex', flexDirection: 'column', gap: '26px', justifyContent: 'center', overflow: 'hidden' }} className="hidden lg:flex">
        <span style={{ position: 'absolute', right: '-120px', top: '-140px', width: '360px', height: '360px', borderRadius: '999px', background: '#72B744', opacity: 0.24 }} />
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '440px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#FFC717' }}>Cuisiniers &amp; commerçants</span>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3vw, 40px)', lineHeight: 1.04, letterSpacing: '-0.025em' }}>
            Développez votre activité avec namke
          </h2>
        </div>
        <div style={{ position: 'relative', width: 'min(320px, 100%)', borderRadius: '220px 220px 34px 220px', overflow: 'hidden' }}>
          <img src={namkeAuthBanner} alt="" style={{ width: '100%', aspectRatio: '4 / 5', objectFit: 'cover' }} />
        </div>
        <ul style={{ position: 'relative', margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '440px' }}>
          <li className="flex gap-3" style={{ fontSize: '15px', lineHeight: 1.5 }}><span style={{ color: '#FFC717', fontWeight: 700 }}>✓</span>Commission 12 %, versements chaque lundi</li>
          <li className="flex gap-3" style={{ fontSize: '15px', lineHeight: 1.5 }}><span style={{ color: '#FFC717', fontWeight: 700 }}>✓</span>Zéro engagement, résiliable à tout moment</li>
          <li className="flex gap-3" style={{ fontSize: '15px', lineHeight: 1.5 }}><span style={{ color: '#FFC717', fontWeight: 700 }}>✓</span>Accompagnement pour le certificat d'hygiène</li>
        </ul>
      </aside>
    </div>
  );
};

export default RestaurantSignup;
